import { useCallback, useEffect, useMemo, useState } from "react";

import { ChevronLeftIcon, ChevronRightIcon } from "components/icons";
import SelectField from "./SelectField";

import { useTranslationContext } from "context/TranslationContext";
import { useRenderStatus } from "hooks/useRenderStatus";
import cn from "lib/cn";

import styles from "./Pagination.module.css";

export interface PaginationProps {
  itemPerPage: number;
  currentPage: number;
  totalItem: number;
  perPageOptions: number[];
  onChangePage?: (newPage: number) => void;
  onChangePerPage?: (perPage: number) => void;
}

const Pagination = ({
  itemPerPage,
  currentPage,
  totalItem,
  perPageOptions,
  onChangePage,
  onChangePerPage,
}: PaginationProps) => {
  const { t } = useTranslationContext();
  const busy = useRenderStatus();
  const [pageRange, setPageRange] = useState<number[]>([]);
  const [_currentPage, setCurrentPage] = useState(0);
  const [startRange, setStartRange] = useState(1);
  const [endRange, setEndRange] = useState(10);

  const totalPage = useMemo(() => {
    if (totalItem < 1) return 0;
    if (totalItem < itemPerPage) return 1;
    return Math.ceil(totalItem / itemPerPage);
  }, [totalItem, itemPerPage]);

  const generatePageRange = useCallback(() => {
    const range = totalPage > 4 ? 4 : totalPage;

    let added = 0;
    if (_currentPage < range) {
      added = range - _currentPage;
    }
    const tmpPageRange = _currentPage + added;

    const startRange = (currentPage - 1) * itemPerPage + 1;
    const endRange = Math.min(currentPage * itemPerPage);
    setStartRange(startRange);
    setEndRange(endRange);

    const newPageRange: number[] = [];
    for (let x = tmpPageRange; x > 0; x--) {
      if (x <= tmpPageRange - range) break;
      newPageRange.push(x);
    }

    setPageRange(newPageRange.reverse());
  }, [_currentPage, totalPage, currentPage, itemPerPage]);
  const handleOnChangePage = (newPage: number) => {
    setCurrentPage(newPage);
    if (onChangePage) onChangePage(newPage);
  };

  const handleOnNext = () => {
    const newPage = _currentPage + 1;
    setCurrentPage(newPage);
    if (onChangePage) onChangePage(newPage);
  };

  const handleOnPrev = () => {
    const newPage = _currentPage - 1;
    setCurrentPage(newPage);
    if (onChangePage) onChangePage(newPage);
  };

  useEffect(() => {
    if (!busy) {
      generatePageRange();
    }
  }, [itemPerPage, generatePageRange, busy]);

  useEffect(() => {
    setCurrentPage(currentPage);
  }, [currentPage]);

  return (
    <div
      className={cn(
        styles.container,
        "absolute bottom-0 text-base text-night right-0 left-0 bg-gray-600"
      )}
    >
      <div className="flex justify-between relative rounded-b-xl items-center w-100 p-2 mb-0 border-t border-line-500 pt-4 px-5 bg-white">
        <div className="flex pb-1">
          {t("showing")} {startRange}-{endRange} {t("of")} {totalItem}{" "}
          {t("items")}
        </div>
        <div className="flex items-center gap-8">
          <div>
            <SelectField
              label="show"
              options={perPageOptions}
              selected={itemPerPage}
              onSelect={onChangePerPage}
            />
          </div>
          <div className="flex items-center gap-4">
            <div
              className={cn(
                _currentPage > 1 ? styles.page : undefined,
                styles.icon_prev,
                _currentPage > 1 ? "text-gray-750" : "text-gray-400"
              )}
              onClick={_currentPage > 1 ? handleOnPrev : undefined}
            >
              <ChevronLeftIcon width={18} height={18} />
            </div>

            {pageRange.map((pageNumber) => {
              return (
                <div
                  key={pageNumber}
                  onClick={
                    pageNumber !== _currentPage
                      ? () => handleOnChangePage(pageNumber)
                      : undefined
                  }
                  className={cn(
                    pageNumber === _currentPage ? styles.active : undefined,
                    pageNumber !== _currentPage ? styles.page : undefined,
                    styles.page_width
                  )}
                >
                  {pageNumber}
                </div>
              );
            })}

            {_currentPage !== totalPage && totalPage > 4 && (
              <>
                <div className={cn([styles.page_width, "pb-2"])}>...</div>
                <div
                  className={cn(styles.page)}
                  onClick={
                    totalPage !== _currentPage
                      ? () => handleOnChangePage(totalPage)
                      : undefined
                  }
                >
                  {totalPage}
                </div>
              </>
            )}
            <div
              className={cn(
                _currentPage >= totalPage ? undefined : styles.page,
                styles.icon_next,
                _currentPage >= totalPage ? "text-gray-400 " : "text-gray-750"
              )}
              onClick={_currentPage >= totalPage ? undefined : handleOnNext}
            >
              <ChevronRightIcon width={18} height={18} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
